import type { ApiInspectionsV2ListParams, InspectionDTO } from 'api';
import { useApiInspectionsV2List } from 'api';
import type { Dayjs } from 'date';
import { useTranslation } from 'i18n';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from 'ui';
import { Button, DataGrid, Page, Section } from 'ui';

import { createFromFilter, createToFilter, TimeFilter } from '../../../time-filter';
import { columns } from './columns';
import { TIME_FILTER_FIELD_NAME } from './constants';

const DEFAULT_PAGE_SIZE = 25;
const NUMERIC_FILTER_FIELDS = new Set<string>(['deal_id', 'loads_count', 'site_material_id']);

const mapFilterModelToParams = (filterModel: GridFilterModel): Partial<ApiInspectionsV2ListParams> => {
  const params: Record<string, unknown> = {};

  filterModel.items.forEach((item) => {
    if (!item.field || item.value === undefined || item.value === null || item.value === '') {
      return;
    }

    if (item.operator !== 'equals' && item.operator !== 'is') {
      return;
    }

    const field = item.field as keyof ApiInspectionsV2ListParams;
    const rawValue = item.value;

    if (NUMERIC_FILTER_FIELDS.has(item.field)) {
      const numericValue = Number(rawValue);

      if (Number.isNaN(numericValue)) {
        return;
      }

      params[field] = numericValue;
      return;
    }

    params[field] = rawValue;
  });

  return params as Partial<ApiInspectionsV2ListParams>;
};

export const InspectionsList = () => {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'deal_id',
      sort: 'desc',
    },
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [timeFilters, setTimeFilters] = useState<Pick<ApiInspectionsV2ListParams, 'time__gte' | 'time__lte'>>({});

  const handleTimeFilterChange = useCallback(({ from, to }: { from: Dayjs; to: Dayjs }) => {
    setTimeFilters({
      time__gte: createFromFilter(from, TIME_FILTER_FIELD_NAME).value as string,
      time__lte: createToFilter(to, TIME_FILTER_FIELD_NAME).value as string,
    });
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const filterParams = useMemo(() => mapFilterModelToParams(filterModel), [filterModel]);

  const orderParam = useMemo(() => {
    const sortItem = sortModel[0];

    if (!sortItem || !sortItem.sort) {
      return undefined;
    }

    const prefix = sortItem.sort === 'desc' ? '-' : '';

    return `${prefix}${sortItem.field}` as ApiInspectionsV2ListParams['order'];
  }, [sortModel]);

  const queryParams = useMemo<ApiInspectionsV2ListParams>(() => {
    const params: ApiInspectionsV2ListParams = {
      page: paginationModel.page + 1,
      page_size: paginationModel.pageSize,
      ...filterParams,
    };

    if (timeFilters.time__gte) {
      params.time__gte = timeFilters.time__gte;
    }

    if (timeFilters.time__lte) {
      params.time__lte = timeFilters.time__lte;
    }

    if (orderParam) {
      params.order = orderParam;
    }

    return params;
  }, [filterParams, orderParam, paginationModel.page, paginationModel.pageSize, timeFilters]);

  const { data, isFetching } = useApiInspectionsV2List(queryParams, {
    query: {
      placeholderData: (previousData) => previousData,
    },
  });

  const rows = data?.results ?? [];
  const rowCount = data?.count ?? 0;

  return (
    <Page>
      <Section
        title={t('inspectionsList.title')}
        action={
          <Button size="medium" component={Link} to="/ground-control/inspections/create">
            {t('inspectionsList.actions.createInspection')}
          </Button>
        }
      >
        <TimeFilter onDateChange={handleTimeFilterChange} />
        <DataGrid<InspectionDTO>
          autoHeight
          columns={columns}
          showColumnVerticalBorder
          showCellVerticalBorder
          filterDebounceMs={500}
          density="standard"
          sx={{ overflowX: 'scroll' }}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={(model) => {
            setFilterModel(model);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          loading={isFetching}
          rowCount={rowCount}
          rows={rows}
          onRowClick={(params) => {
            navigate(`/ground-control/inspections/${params.row.id}`);
          }}
        />
      </Section>
    </Page>
  );
};
